/**
 * @fileoverview External School Select Component
 * @description Enterprise-grade autocomplete for 123k+ US schools
 * Features: Server-side search, debounce, geolocation
 */

import { useEffect, forwardRef } from 'react'
import { Select, Loader, Text, Group, Stack, Badge } from '@mantine/core'
import { IconMapPin } from '@tabler/icons-react'
import { useSchoolSearch } from './useSchoolSearch'
import { useGeolocation } from './useGeolocation'
import type { ExternalSchoolSelectProps, SchoolSelectItem } from './types'

/**
 * Custom item component for Select dropdown
 */
interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string
  description?: string
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ label, description, ...others }, ref) => (
    <div ref={ref} {...others}>
      <Text size='sm' weight={500} inherit>{label}</Text>
      {description && (
        <Text size='xs' opacity={0.7} inherit>{description}</Text>
      )}
    </div>
  )
)

SelectItem.displayName = 'SelectItem'

/**
 * External School Select Component
 * Optimized for large datasets with server-side search
 */
export const ExternalSchoolSelect = ({
  value,
  onChange,
  error,
  disabled = false,
  placeholder,
  label,
  required = false,
  size = 'md',
}: ExternalSchoolSelectProps) => {
  const { zipcode, isUSA, loading: geoLoading } = useGeolocation(true)
  const {
    options,
    loading: searchLoading,
    searchSchools,
    fetchInitialSchools,
    findSchoolById,
  } = useSchoolSearch()

  // Fetch initial schools based on geolocation
  useEffect(() => {
    if (!geoLoading) {
      fetchInitialSchools(isUSA ? zipcode : null)
    }
  }, [geoLoading, isUSA, zipcode, fetchInitialSchools])

  // Handle search input change
  const handleSearchChange = (query: string) => {
    searchSchools(query)
  }

  // Handle selection change
  const handleChange = (selectedValue: string | null) => {
    const school = selectedValue ? findSchoolById(selectedValue) : null
    onChange(selectedValue, school)
  }

  // Dynamic placeholder
  const dynamicPlaceholder = placeholder
    || (geoLoading ? 'Detecting location...' : 'Search for a school...')

  // Prepare data for Mantine Select
  const selectData = options.map((item: SchoolSelectItem) => ({
    value: item.value,
    label: item.label,
    description: item.description,
  }))

  return (
    <Stack spacing={4}>
      {isUSA && zipcode && !geoLoading && (
        <Group spacing={4}>
          <Badge
            size='sm'
            variant='light'
            leftSection={<IconMapPin size={12} />}
          >
            Schools near {zipcode}
          </Badge>
        </Group>
      )}

      <Select
        label={label}
        placeholder={dynamicPlaceholder}
        data={selectData}
        value={value}
        onChange={handleChange}
        onSearchChange={handleSearchChange}
        searchable
        clearable
        nothingFound={searchLoading ? 'Searching...' : 'No schools found'}
        disabled={disabled || geoLoading}
        error={error}
        required={required}
        size={size}
        maxDropdownHeight={320}
        itemComponent={SelectItem}
        rightSection={searchLoading || geoLoading ? <Loader size='xs' /> : null}
        filter={() => true} // Disable client-side filtering (server handles it)
        styles={{
          dropdown: {
            maxHeight: 320,
          },
        }}
      />
    </Stack>
  )
}
